import { useState } from 'react'
import { useUpdateCourse, useDeleteCourse } from '../hooks/useCourses'
import type { CourseContent } from '../types'
import { shareLink } from '../utils/shareLink'

interface Props {
  course: CourseContent
  onClose: () => void
  onDeleted: () => void
}

export default function CourseSettingsModal({ course, onClose, onDeleted }: Props) {
  const [title, setTitle] = useState(course.title)
  const [description, setDescription] = useState(course.description ?? '')
  const [isPublic, setIsPublic] = useState(course.is_public)
  const [shareTooltip, setShareTooltip] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const updateCourse = useUpdateCourse()
  const deleteCourse = useDeleteCourse()

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    updateCourse.mutate(
      { id: course.id, title: title.trim(), description: description.trim() || undefined, is_public: isPublic },
      { onSuccess: () => onClose() },
    )
  }

  async function handleCopyLink() {
    if (!course.share_token) return
    const result = await shareLink({
      title: course.title,
      url: `${window.location.origin}/shared/course/${course.share_token}`,
    })
    if (result === 'clipboard') {
      setShareTooltip(true)
      setTimeout(() => setShareTooltip(false), 2000)
    }
  }

  function handleDelete() {
    deleteCourse.mutate(course.id, { onSuccess: () => { onClose(); onDeleted() } })
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)', width: '100%', maxWidth: 480 }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Course Settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 22, padding: 4, lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSave} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Title */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Title <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')} />
          </div>

          {/* Description */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              placeholder="Optional description…"
              style={{ ...inputStyle, resize: 'vertical' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')} />
          </div>

          {/* Public toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
            <div onClick={() => setIsPublic(!isPublic)} style={{ width: 40, height: 22, borderRadius: 11, background: isPublic ? 'var(--accent)' : 'var(--border)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 3, left: isPublic ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
            </div>
            <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Make course public</span>
          </label>

          {/* Share link */}
          {course.share_token && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {window.location.origin}/shared/course/{course.share_token}
              </span>
              <div style={{ position: 'relative' }}>
                <button type="button" onClick={handleCopyLink} style={{ padding: '4px 12px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                  Copy
                </button>
                {shareTooltip && (
                  <div style={{ position: 'absolute', top: -28, right: 0, background: 'var(--text-primary)', color: 'var(--bg-page)', fontSize: 11, padding: '3px 8px', borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap' }}>Copied!</div>
                )}
              </div>
            </div>
          )}

          {/* Save / Cancel */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            {confirmDelete ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--danger)' }}>Delete course?</span>
                <button type="button" onClick={handleDelete} disabled={deleteCourse.isPending}
                  style={{ padding: '6px 14px', background: 'var(--danger-fill)', color: 'var(--text-on-dark)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer' }}>
                  Confirm
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)}
                  style={{ padding: '6px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)}
                style={{ padding: '8px 16px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 13, cursor: 'pointer', color: 'var(--danger)' }}>
                Delete Course
              </button>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose}
                style={{ padding: '10px 20px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={!title.trim() || updateCourse.isPending}
                style={{ padding: '10px 24px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: !title.trim() ? 0.5 : 1 }}>
                {updateCourse.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }
const inputStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'var(--font-ui)', width: '100%', boxSizing: 'border-box' }
